import { useCallback, type Dispatch, type MutableRefObject, type RefObject, type SetStateAction } from 'react';
import type { TemplateCollections } from '../types/editor';
import { mapServerTemplatesToList } from '../utils/templateCollections';

export function buildAuthContextFingerprint(payload: Record<string, unknown>): string {
  const userId = payload?.userId ?? '';
  const accessToken = typeof payload?.accessToken === 'string' ? payload.accessToken : '';
  const supabaseUrl = typeof payload?.supabaseUrl === 'string' ? payload.supabaseUrl : '';
  const anonKey = typeof payload?.anonKey === 'string' ? payload.anonKey : '';
  return JSON.stringify([userId, accessToken, supabaseUrl, anonKey]);
}

interface UseParentContextMessageHandlersArgs {
  templatesRequestPendingRef: RefObject<boolean>;
  lastAuthContextFingerprintRef: MutableRefObject<string | null>;
  hasTemplatesLoaded: boolean;
  setTemplatesByCategory: Dispatch<SetStateAction<TemplateCollections>>;
  setHasTemplatesLoaded: Dispatch<SetStateAction<boolean>>;
  setIsLoadingTemplates: Dispatch<SetStateAction<boolean>>;
  loadTemplates: () => void;
  setCurrentUsername: Dispatch<SetStateAction<string | null>>;
  setCurrentUserRole: Dispatch<SetStateAction<string | null>>;
  setSnapLinkPlan: Dispatch<SetStateAction<string | null>>;
  emitImageLibraryEvent: (detail: Record<string, unknown>) => void;
  isBridgeAuthReady: boolean;
  setIsBridgeAuthReady: Dispatch<SetStateAction<boolean>>;
  syncSnapAuthTokenFromStorage: () => void;
}

export function resolveTokenFromAuthContext(accessToken: unknown, anonKey: unknown): string | null {
  return typeof accessToken === 'string' && accessToken.trim().length
    ? accessToken.trim()
    : typeof anonKey === 'string' && anonKey.trim().length
      ? anonKey.trim()
      : null;
}

export function resolveUsernameFromAuthContext(username: unknown): string | null {
  return typeof username === 'string' && username.trim().length
    ? username.trim()
    : null;
}

export function resolveRoleFromAuthContext(role: unknown): string | null {
  return typeof role === 'string' && role.trim().length
    ? role.trim()
    : null;
}

export function resolvePlanFromAuthContext(
  licensePlan: unknown,
  snapLinkEnabled: unknown,
): { shouldSet: boolean; plan: string | null } {
  if (typeof licensePlan === 'string' && licensePlan.trim().length) {
    return { shouldSet: true, plan: licensePlan.trim() };
  }
  if (typeof snapLinkEnabled === 'boolean') {
    return { shouldSet: true, plan: snapLinkEnabled ? null : 'solo' };
  }
  return { shouldSet: false, plan: null };
}

export function mergeTemplatesFromFetchedPayload(
  previous: TemplateCollections,
  payload: Record<string, unknown>,
): TemplateCollections {
  const rawByVisibility =
    payload && typeof payload.templatesByVisibility === 'object' && payload.templatesByVisibility !== null
      ? payload.templatesByVisibility
      : {};

  const hasPrivate = Object.prototype.hasOwnProperty.call(rawByVisibility, 'private');
  const hasSnaplayer = Object.prototype.hasOwnProperty.call(rawByVisibility, 'snaplayer');
  const hasCommunity = Object.prototype.hasOwnProperty.call(rawByVisibility, 'community');

  const myTemplates = hasPrivate
    ? mapServerTemplatesToList((rawByVisibility as Record<string, unknown>).private, previous.myTemplates)
    : Array.isArray(payload.templates)
      ? mapServerTemplatesToList(payload.templates, previous.myTemplates)
      : previous.myTemplates;

  const snaplayerTemplates = hasSnaplayer
    ? mapServerTemplatesToList((rawByVisibility as Record<string, unknown>).snaplayer, previous.snaplayerTemplates)
    : previous.snaplayerTemplates;

  const communityTemplates = hasCommunity
    ? mapServerTemplatesToList((rawByVisibility as Record<string, unknown>).community, previous.communityTemplates)
    : previous.communityTemplates;

  return {
    myTemplates,
    snaplayerTemplates,
    communityTemplates,
  };
}

/**
 * Extracted handlers for `templatesFetched` and `authContext` postMessage events.
 *
 * `templatesFetched` – maps server template lists into the categorised collections.
 * `authContext` – bootstraps authentication globals and triggers a template reload.
 */
export function useParentContextMessageHandlers({
  templatesRequestPendingRef,
  lastAuthContextFingerprintRef,
  hasTemplatesLoaded,
  setTemplatesByCategory,
  setHasTemplatesLoaded,
  setIsLoadingTemplates,
  loadTemplates,
  setCurrentUsername,
  setCurrentUserRole,
  setSnapLinkPlan,
  emitImageLibraryEvent,
  isBridgeAuthReady,
  setIsBridgeAuthReady,
  syncSnapAuthTokenFromStorage,
}: UseParentContextMessageHandlersArgs) {
  const handleTemplatesFetchedMessage = useCallback(
    (payload: Record<string, unknown>) => {
      setTemplatesByCategory((previous) => mergeTemplatesFromFetchedPayload(previous, payload));
      setHasTemplatesLoaded(true);
      setIsLoadingTemplates(false);
      templatesRequestPendingRef.current = false;
    },
    [setTemplatesByCategory, setHasTemplatesLoaded, setIsLoadingTemplates, templatesRequestPendingRef],
  );

  const handleAuthContextMessage = useCallback(
    (payload: Record<string, unknown>) => {
      const {
        userId,
        isDev,
        supabaseUrl,
        anonKey,
        serviceRoleKey,
        accessToken,
        username,
        role,
        licensePlan,
        snapLinkEnabled,
      } = payload || {};

      (window as any).authContext = payload || null;
      window.currentUserId = userId as string | undefined;

      if (typeof anonKey === 'string' && anonKey.trim().length) {
        window.SUPABASE_ANON_KEY = anonKey.trim();
      }

      if (isDev && typeof serviceRoleKey === 'string' && serviceRoleKey.trim().length) {
        window.SUPABASE_SERVICE_ROLE_KEY = serviceRoleKey.trim();
      } else {
        delete (window as any).SUPABASE_SERVICE_ROLE_KEY;
      }

      if (typeof supabaseUrl === 'string' && supabaseUrl.trim().length) {
        (window as any).SUPABASE_URL = supabaseUrl.trim();
      }

      const nextFingerprint = buildAuthContextFingerprint(payload || {});
      const prevFingerprint = lastAuthContextFingerprintRef.current;
      const shouldReloadTemplates = !hasTemplatesLoaded || prevFingerprint !== nextFingerprint;
      lastAuthContextFingerprintRef.current = nextFingerprint;

      if (shouldReloadTemplates) {
        if ((window as any)?.SNAP_DEBUG) {
          console.log('[authContext] Reloading templates (fingerprint changed or initial load)', { userId });
        }
        loadTemplates();
      } else if ((window as any)?.SNAP_DEBUG) {
        console.log('[authContext] Skipping template reload (auth fingerprint unchanged)', { userId });
      }

      const tokenFromContext = resolveTokenFromAuthContext(accessToken, anonKey);

      if (tokenFromContext) {
        (window as any).SNAP_AUTH_TOKEN = tokenFromContext;
      } else {
        syncSnapAuthTokenFromStorage();
      }

      const normalizedUsername = resolveUsernameFromAuthContext(username);
      setCurrentUsername(normalizedUsername);
      window.currentUsername = normalizedUsername;

      const normalizedRole = resolveRoleFromAuthContext(role);
      setCurrentUserRole(normalizedRole);
      (window as any).currentUserRole = normalizedRole;

      const planResolution = resolvePlanFromAuthContext(licensePlan, snapLinkEnabled);
      if (planResolution.shouldSet) {
        setSnapLinkPlan(planResolution.plan);
      }

      if ((window as any)?.SNAP_DEBUG) console.log('Auth context received:', {
        userId,
        isDev,
        hasServiceKey: !!serviceRoleKey,
        hasAnonKey: !!anonKey,
        hasAccessToken: !!accessToken,
        snapAuthToken: !!(window as any).SNAP_AUTH_TOKEN,
        licensePlan: planResolution.plan,
      });

      emitImageLibraryEvent({
        source: 'auth-context',
        ready: !!(window as any).SNAP_AUTH_TOKEN,
        via: 'postMessage',
      });

      if (!isBridgeAuthReady) {
        setIsBridgeAuthReady(true);
      }
    },
    [
      hasTemplatesLoaded,
      lastAuthContextFingerprintRef,
      loadTemplates,
      setCurrentUsername,
      setCurrentUserRole,
      setSnapLinkPlan,
      emitImageLibraryEvent,
      isBridgeAuthReady,
      setIsBridgeAuthReady,
      syncSnapAuthTokenFromStorage,
    ],
  );

  return { handleTemplatesFetchedMessage, handleAuthContextMessage };
}
